/**
 * Events that get sent to GA through GTM
 * @author juanmiguelrubio
 * @requires gtm.js
 */

import moment from 'moment';
import dlPush from 'gtm';
import { normalizeProperty } from 'ec.utils';

/**
 * Sets the user type
 *
 * @param {String} userType Registered | Anonymous
 */
export function setUserType(userType) {
  dlPush({
    userType: normalizeProperty(userType),
  });
}

/**
 * Sends the Search event to GTM
 *  search properties:
 *    id - Search unique id
 *    passengers - Total number of passengers
 *    adult - Number of adult passengers
 *    child - Number of child passengers
 *    infant - Number of infant passengers
 *    originName - Origin city name
 *    destinationName - Destination city name
 *    roundTrip - Whether the user searched for a round trip
 *    departureDate - Anything that can be parsed with moment
 *    returnDate - Anything that can be parsed with moment
 *
 * @param {Object} search Search related data to track
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackSearch(search, callback) {
  const {
    id,
    passengers,
    adult,
    child,
    infant,
    originName,
    destinationName,
    originTerminalName,
    destinationTerminalName,
    roundTrip,
    departureDate,
    returnDate,
  } = search;
  const departureDelta = moment
    .duration(departureDate - moment().startOf('day'))
    .asDays();

  dlPush(
    {
      event: 'Search',
      label: id,
      value: passengers,
      origin: originName,
      destination: destinationName,
      originTerminal: originTerminalName,
      destinationTerminal: destinationTerminalName,
      tripType: roundTrip ? 'Round' : 'Single',
      departureDate: moment(departureDate).format('YYYY-MM-DD'),
      returnDate: roundTrip ? moment(returnDate).format('YYYY-MM-DD') : 'NA',
      departureDelta,
      adult,
      child,
      infant,
    },
    callback
  );
}

/**
 * Sends a Sign Up event to GTM
 *
 * @param {String} action Sign up to: Price Alert | Newsletter | Account
 * @param {String} label If the user signed up for PA or Newsletter:
 *                         - Sign up location: Pop Up | Blog | ...
 *                       If the user signed up for a new account
 *                         - Sign up method: google-oauth2 | auth0
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackSignUp(action, label, callback) {
  dlPush(
    {
      event: 'Sign Up',
      action: normalizeProperty(action),
      label: normalizeProperty(label),
    },
    callback
  );
}

/**
 * Sends a Log In event to GTM
 *
 * @param {String} action Login type: google-oauth2 | auth0
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackLogIn(action, callback) {
  dlPush(
    {
      event: 'Log In',
      action: normalizeProperty(action),
    },
    callback
  );
}

/**
 * Sends a Contact Attempt event to GTM
 *
 * @param {String} action Contact method: Call | Chat
 * @param {String} location URL path where the contact attempt was made
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackContactAttempt(action, location, callback) {
  dlPush(
    {
      event: 'Contact Attempt',
      action: normalizeProperty(action),
      label: normalizeProperty(location),
    },
    callback
  );
}

/**
 * Sends an Outbound Links event to GTM
 *
 * @param {String} action Google Ads action property
 * @param {String} label Google Ads label property
 * @param {Integer} value Google Ads value property
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackOutboundLinks(
  action,
  label = null,
  value = null,
  callback
) {
  const gaAction = normalizeProperty(action);
  dlPush(
    {
      event: 'Outbound Links',
      action: gaAction,
      label:
        gaAction === 'Purchase Redirect' ? label : normalizeProperty(label),
      value,
    },
    callback
  );
}

/**
 * Sends a Checkout Errors event to GTM. It also tracks the funnel step where
 * the error ocurred, the step number depends on the funnel type:
 *  Single or round trip with no seat selection
 *    (1) passengers (2) payment
 *  Single or round trip with seat selection for just one trip
 *    (1) passengers (2) <departure|return> seat selection 3) payment
 *  Round trip with seat selection for both trips
 *    (1) passengers (2) departure seat selection 3) return seat selection 4) payment
 *
 * error properties:
 *  fieldName - Field with error: firstname, email, phone, etc
 *  fieldError - Error description: invalid email, required, etc
 *  bankName - Credit card issuer (Payment step only)
 *  cardBrand - Visa, mastercard, etc (Payment step only)
 *  paymentType - credit_card (Payment step only)
 *  cardError - Openpay API error (Payment step only)
 *
 * @param {Number} funnelStep 1..4
 * @param {Object} error Object with the error details
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackCheckoutErrors(funnelStep, error, callback) {
  const {
    fieldName,
    fieldError,
    bankName,
    cardBrand,
    paymentType,
    cardError,
  } = error;
  const stepName = `paso${funnelStep}`;

  dlPush(
    {
      event: 'Checkout Errors',
      action: `${stepName}${fieldName}`,
      label: fieldError,
      bankName: bankName ? normalizeProperty(bankName) : 'NA',
      cardBrand: cardBrand ? normalizeProperty(cardBrand) : 'NA',
      paymentType: paymentType ? normalizeProperty(paymentType) : 'NA',
      cardError: cardError || 'NA',
    },
    callback
  );
}

/**
 * Sends a Purchase On Hold event to GTM
 *
 * @param {String} token Purchase token
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackPurchaseOnHold(token, callback) {
  dlPush(
    {
      event: 'Purchase On Hold',
      action: token,
    },
    callback
  );
}

/**
 * Sends a help event to GTM when a user clicks on a section in
 * https://www.reservamos.mx/como-usar-reservamos sidebar
 *
 * @param {String} section Sidebar section
 * @param {Function} callback [optional] A function to call after the data has been sent to GA
 */
export function trackHelp(section, callback) {
  dlPush(
    {
      event: 'Help',
      action: section,
    },
    callback
  );
}



// WEBPACK FOOTER //
// ./ga.events.js